import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { SmartImage, StoryImage } from "./Media";
import { EmptyState, ArrowLink } from "./Content";

const AUTOPLAY_MS = 2000;

export const FeaturedCarousel = ({ slides, heading, headingAction, action }) => {
  const [index, setIndex] = useState(0);
  const touchX = useRef(null);
  const [hover, setHover] = useState(false);
  const [focused, setFocused] = useState(false);
  const reduced = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  const playing = slides.length > 1 && !hover && !focused && !reduced;

  useEffect(() => {
    if (!playing) return undefined;
    const id = setInterval(() => setIndex((i) => (i + 1) % slides.length), AUTOPLAY_MS);
    return () => clearInterval(id);
  }, [playing, slides.length]);

  if (!slides.length) {
    return (
      <EmptyState
        title="No stories yet"
        message="Published Research in Namma Ooru will appear here."
        testId="carousel-empty-state"
        action={<ArrowLink to="/research" testId="carousel-empty-research-link">Browse Research in Namma Ooru</ArrowLink>}
      />
    );
  }

  const count = slides.length;
  const go = (d) => setIndex((i) => (i + d + count) % count);
  const s = slides[Math.min(index, count - 1)];
  const onKey = (e) => {
    const map = { ArrowLeft: () => go(-1), ArrowRight: () => go(1), Home: () => setIndex(0), End: () => setIndex(count - 1) };
    if (map[e.key] && e.target === e.currentTarget) {
      e.preventDefault();
      map[e.key]();
    }
  };
  const onTouchEnd = (e) => {
    if (touchX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
    touchX.current = null;
  };

  return (
    <section aria-roledescription="carousel" aria-labelledby="featured-heading" aria-describedby="carousel-help" onKeyDown={onKey} tabIndex={0} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} onFocus={() => setFocused(true)} onBlur={(e) => !e.currentTarget.contains(e.relatedTarget) && setFocused(false)} data-playing={playing ? "true" : "false"} data-testid="featured-carousel">
      <div className="mb-5 flex items-end justify-between gap-4 border-t border-ink pt-4">
        <div className="flex flex-wrap items-center gap-4">
          <h2 id="featured-heading" className="eyebrow text-brand" data-testid="carousel-heading">{heading}</h2>
          {headingAction}
        </div>
        <div className="flex items-center gap-6">
          <p className="hidden whitespace-nowrap text-sm font-bold tabular-nums text-ink sm:block" data-testid="carousel-counter">
            {String(index + 1).padStart(2, "0")} <span className="text-ink-muted">/ {String(count).padStart(2, "0")}</span>
          </p>
          {action && <div className="whitespace-nowrap">{action}</div>}
        </div>
      </div>
      <p id="carousel-help" className="sr-only">Slides advance automatically and pause on hover or focus. Use the previous and next buttons, or the left and right arrow keys when the carousel is focused, to change slides.</p>
      <p className="sr-only" aria-live={playing ? "off" : "polite"} data-testid="carousel-live">{`Slide ${index + 1} of ${count}: ${s.title}`}</p>

      <div className="grid bg-brand-black text-white" onTouchStart={(e) => { touchX.current = e.touches[0].clientX; setFocused(true); }} onTouchEnd={onTouchEnd}>
        {slides.map((sl, i) => <Slide key={sl.key} s={sl} active={i === index} label={`${i + 1} of ${count}`} />)}
      </div>
      <div className="flex items-center justify-between gap-4 border-t border-white/15 bg-brand-black px-6 py-5 text-white sm:px-10 lg:px-12">
              <div className="flex items-center gap-1">
                {slides.map((sl, i) => (
                  <button key={sl.key} type="button" onClick={() => setIndex(i)} aria-label={`Go to slide ${i + 1}: ${sl.title}`} aria-current={i === index ? "true" : undefined} className="flex h-11 items-center px-1" data-testid={`carousel-dot-${i}`}>
                    <span className={`block h-2 rounded-full transition-[width,background-color] duration-300 ${i === index ? "w-8 bg-brand-green" : "w-2 bg-white/40 hover:bg-white/70"}`} aria-hidden="true" />
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => go(-1)} aria-label="Previous slide" className="carousel-btn" data-testid="carousel-prev-button"><ChevronLeft className="h-5 w-5" aria-hidden="true" /></button>
                <button type="button" onClick={() => go(1)} aria-label="Next slide" className="carousel-btn" data-testid="carousel-next-button"><ChevronRight className="h-5 w-5" aria-hidden="true" /></button>
              </div>
      </div>
    </section>
  );
};

// All slides share one grid cell so the carousel keeps the tallest slide's height and never jumps.
const Slide = ({ s, active, label }) => {
  const anim = active ? "slide-in" : "";
  return (
    <div role="group" aria-roledescription="slide" aria-label={label} aria-hidden={!active} inert={active ? undefined : ""} className={`col-start-1 row-start-1 grid lg:grid-cols-12 ${active ? "" : "invisible"}`} data-testid={active ? `carousel-slide-${s.kind}` : undefined}>
      {s.kind === "story" ? (
        <StoryImage src={s.image} alt={s.imageAlt} className={`${anim} self-center lg:col-span-7`} testId={active ? "carousel-slide-image" : undefined} />
      ) : (
        <SmartImage src={s.image} alt={s.imageAlt} ratio="aspect-[16/10] lg:aspect-auto" className={`${anim} lg:col-span-7 lg:min-h-[540px]`} testId={active ? "carousel-slide-image" : undefined} />
      )}
      <div className={`${anim} p-6 sm:p-10 lg:col-span-5 lg:p-12`}>
        <span className="rounded-full bg-brand-green px-3 py-1 text-[11px] font-extrabold uppercase tracking-[0.14em] text-brand-black" data-testid={active ? "carousel-slide-type" : undefined}>{s.typeLabel}</span>
        <h3 className="mt-6 text-2xl font-extrabold leading-tight tracking-tight sm:text-3xl lg:text-[2.5rem] lg:leading-[1.1]" data-testid={active ? "carousel-slide-title" : undefined}>{s.title}</h3>
        <ul className="mt-5 flex flex-wrap gap-x-5 gap-y-1 text-sm text-white/70" data-testid={active ? "carousel-slide-meta" : undefined}>
          {s.category && <li className="font-bold uppercase tracking-[0.12em] text-brand-green">{s.category}</li>}
          {s.dateLabel && <li><time dateTime={s.dateTime}>{s.dateLabel}</time></li>}
        </ul>
        <p className="mt-5 max-w-md leading-relaxed text-white/80" data-testid={active ? "carousel-slide-summary" : undefined}>{s.summary}</p>
        <Link to={s.to} tabIndex={active ? undefined : -1} className="group mt-8 inline-flex items-center gap-2 border-b-2 border-brand-green pb-1 text-sm font-bold text-white transition-colors hover:text-brand-green" data-testid={active ? "carousel-slide-link" : undefined}>
          {s.cta}<span className="sr-only">: {s.title}</span>
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
        </Link>
      </div>
    </div>
  );
};
